import { useState } from "react";
import { api, formatApiError } from "../../lib/api";

const inputCls =
  "w-full border border-sage bg-white px-4 py-3 text-sm text-charcoal focus:border-forest focus:ring-1 focus:ring-forest rounded-sm";

export function ContactForm({ kind = "contact", interests = false }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", interest: "", message: "", website: "" });
  const [state, setState] = useState({ status: "idle", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const payload = kind === "volunteer"
        ? { name: form.name, email: form.email, interest: form.interest, message: form.message, website: form.website }
        : { name: form.name, email: form.email, subject: form.subject, message: form.message, website: form.website };
      await api.post(`/public/${kind}`, payload);
      setState({
        status: "success",
        message: kind === "volunteer"
          ? "Thank you for your interest in volunteering. We will be in touch using the details you provided."
          : "Thank you for your message. We aim to respond to genuine enquiries as soon as we can.",
      });
      setForm({ name: "", email: "", subject: "", interest: "", message: "", website: "" });
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  if (state.status === "success") {
    return (
      <div className="bg-white border border-sage p-8" data-testid={`${kind}-form-success`}>
        <h3 className="font-serif text-2xl font-semibold text-forest">Message received</h3>
        <p className="mt-3 text-sm text-charcoal/80 leading-relaxed">{state.message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-sandstone p-8 space-y-5" data-testid={`${kind}-form`} noValidate={false}>
      {/* Honeypot — invisible to people, catches simple bots */}
      <div className="hidden" aria-hidden="true">
        <label>Website <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor={`${kind}-name`} className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Name</label>
          <input id={`${kind}-name`} required maxLength={120} value={form.name} onChange={set("name")}
            className={inputCls} data-testid={`${kind}-name-input`} autoComplete="name" />
        </div>
        <div>
          <label htmlFor={`${kind}-email`} className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Email address</label>
          <input id={`${kind}-email`} type="email" required value={form.email} onChange={set("email")}
            className={inputCls} data-testid={`${kind}-email-input`} autoComplete="email" />
        </div>
      </div>
      {kind === "contact" ? (
        <div>
          <label htmlFor="contact-subject" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Enquiry subject</label>
          <input id="contact-subject" required maxLength={160} value={form.subject} onChange={set("subject")}
            className={inputCls} data-testid="contact-subject-input" />
        </div>
      ) : (
        <div>
          <label htmlFor="volunteer-interest" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Area of interest (optional)</label>
          <input id="volunteer-interest" maxLength={200} value={form.interest} onChange={set("interest")}
            placeholder="e.g. fundraising, administration, community outreach"
            className={inputCls} data-testid="volunteer-interest-input" />
        </div>
      )}
      <div>
        <label htmlFor={`${kind}-message`} className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Message</label>
        <textarea id={`${kind}-message`} required rows={6} maxLength={4000} value={form.message} onChange={set("message")}
          className={inputCls} data-testid={`${kind}-message-input`} />
      </div>
      {state.status === "error" && (
        <p className="text-sm text-red-700" role="alert" data-testid={`${kind}-form-error`}>{state.message}</p>
      )}
      <p className="text-xs text-charcoal/60 leading-relaxed">
        The information you provide is used only to respond to your enquiry and is handled in
        line with our <a href="/privacy-policy" className="underline text-forest">privacy notice</a>.
        It is never shared publicly or sold.
      </p>
      <button type="submit" disabled={state.status === "loading"}
        data-testid={`${kind}-submit-button`}
        className="bg-forest text-white font-medium px-8 py-3 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
        {state.status === "loading" ? "Sending…" : kind === "volunteer" ? "Send Volunteer Enquiry" : "Send Message"}
      </button>
    </form>
  );
}
